'use client';

import { useState } from 'react';
import Image from 'next/image';
import { User } from 'lucide-react';

interface UserAvatarProps {
  photoURL?: string | null;
  displayName?: string | null;
  email?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
  clickable?: boolean;
  priority?: boolean; // For above-the-fold avatars
}

export function UserAvatar({
  photoURL,
  displayName,
  email,
  size = 'md',
  className = '',
  onClick,
  clickable = false,
  priority = false
}: UserAvatarProps) {
  const [imageError, setImageError] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  const containerClasses = `${clickable || onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`;

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-7 h-7 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-20 h-20 text-xl'
  };

  const iconSizes = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8'
  };

  const pixelSizes = {
    xs: 24,
    sm: 28,
    md: 40,
    lg: 48,
    xl: 80
  };

  // Generate initials from display name or email
  const getInitials = () => {
    if (displayName) {
      const names = displayName.trim().split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      }
      return displayName.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '';
  };

  // Generate a consistent color based on the user's identifier
  const getBackgroundColor = () => {
    const str = displayName || email || '';
    if (!str) return 'bg-gray-400';

    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-cyan-500'
    ];

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const initials = getInitials();
  const bgColor = getBackgroundColor();

  // Show image if we have a URL and it hasn't errored
  if (photoURL && !imageError) {
    return (
      <div
        className={`${sizeClasses[size]} relative rounded-full overflow-hidden ${containerClasses} ${className}`}
        onClick={handleClick}
      >
        <Image
          src={photoURL}
          alt={displayName || 'User'}
          width={pixelSizes[size]}
          height={pixelSizes[size]}
          className="object-cover"
          onError={() => setImageError(true)}
          loading={priority ? "eager" : "lazy"}
          priority={priority}
          quality={75}
          sizes={`${pixelSizes[size]}px`}
        />
      </div>
    );
  }

  // Show initials if we have them
  if (initials) {
    return (
      <div
        className={`${sizeClasses[size]} ${bgColor} rounded-full flex items-center justify-center text-white font-medium ${containerClasses} ${className}`}
        onClick={handleClick}
      >
        {initials}
      </div>
    );
  }

  // Fallback to icon
  return (
    <div
      className={`${sizeClasses[size]} bg-gray-300 rounded-full flex items-center justify-center ${containerClasses} ${className}`}
      onClick={handleClick}
    >
      <User className={`${iconSizes[size]} text-gray-600`} />
    </div>
  );
}